import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
} from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ArrowLeft, ExternalLink, Reply, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  type AdminFeedbackDetail,
  type FeedbackStatus,
  useAdminFeedbackDetail,
  useReplyAdminFeedback,
  useUpdateAdminFeedback,
} from "@/admin/services";

import { Card } from "../../../components/ui/card";
import { PageHeader } from "../../_components/page-header";

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: "NEW", label: "New" },
  { value: "READ", label: "Read" },
  { value: "REPLIED", label: "Replied" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "ARCHIVED", label: "Archived" },
];

const KIND_LABELS: Record<AdminFeedbackDetail["kind"], string> = {
  GENERAL: "Idea / Praise",
  NPS_FOLLOWUP: "NPS",
  EXIT_SURVEY: "Exit",
  BUG_REPORT: "Bug",
};

const Row = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-[12px] uppercase tracking-[0.06em] text-subtle">{label}</dt>
    <dd className={cn("text-[13px] text-foreground", mono && "mono")}>{value}</dd>
  </div>
);

export const AdminFeedbackDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminFeedbackDetail(id);

  const updateMutation = useUpdateAdminFeedback(id ?? "");
  const replyMutation = useReplyAdminFeedback(id ?? "");

  const [status, setStatus] = useState<FeedbackStatus>("NEW");
  const [adminNote, setAdminNote] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);

  // Mirror server state into the local edit form whenever a fresh row arrives
  // — but only when a different feedback row loads, so we don't blow away
  // unsaved tweaks every time the cache refetches.
  useEffect(() => {
    if (!data) return;
    setStatus(data.status);
    setAdminNote(data.adminNote ?? "");
    setTagsRaw(data.tags.join(", "));
  }, [data?.id, data?.updatedAt, data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    if (status !== data.status) return true;
    if ((adminNote ?? "") !== (data.adminNote ?? "")) return true;
    const parsedTags = tagsRaw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (parsedTags.join("|") !== data.tags.join("|")) return true;
    return false;
  }, [data, status, adminNote, tagsRaw]);

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Loading…" trail={["Admin", "Feedback"]} />
        <Skeleton className="h-32 w-full" />
      </>
    );
  }

  const recipientEmail = data.email ?? data.user?.email ?? null;
  const canReply = Boolean(recipientEmail);
  const heading = data.message.split("\n")[0]?.slice(0, 60) || `Feedback ${data.id.slice(0, 8)}`;

  const handleSave = () => {
    const parsedTags = tagsRaw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    updateMutation.mutate({
      status,
      adminNote: adminNote.trim().length === 0 ? null : adminNote.trim(),
      tags: parsedTags,
    });
  };

  const handleMarkAs = (next: FeedbackStatus) => {
    if (next === data.status) return;
    setStatus(next);
    updateMutation.mutate({ status: next });
  };

  const handleSendReply = () => {
    setReplyError(null);
    if (replySubject.trim().length < 3) {
      setReplyError("Subject must be at least 3 characters.");
      return;
    }
    if (replyBody.trim().length < 10) {
      setReplyError("Reply body must be at least 10 characters.");
      return;
    }
    replyMutation.mutate(
      {
        subject: replySubject.trim(),
        body: replyBody.trim(),
      },
      {
        onSuccess: () => {
          setReplyOpen(false);
          setReplySubject("");
          setReplyBody("");
        },
        onError: (error: unknown) => {
          const message =
            (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
            "Couldn't send reply. Try again.";
          setReplyError(message);
        },
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>Feedback · {heading} · Admin</title>
      </Helmet>

      <PageHeader
        title={heading}
        trail={["Admin", "Feedback"]}
        description={`${KIND_LABELS[data.kind]} · ${data.surface} · ${new Date(
          data.createdAt,
        ).toLocaleString()}`}
        actions={
          <>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/feedback" className="gap-1">
                <ArrowLeft className="size-3.5" />
                Back
              </Link>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setReplySubject(`Re: your feedback on ${data.surface}`);
                setReplyOpen(true);
              }}
              disabled={!canReply}
              title={canReply ? "Reply via email" : "No email or linked user — cannot reply"}
            >
              <Reply className="mr-1.5 size-3.5" />
              Reply
            </Button>
          </>
        }
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: the message itself + context. */}
        <div className="space-y-4 lg:col-span-2">
          <Card title="Message">
            <div className="space-y-3">
              {data.rating !== null && (
                <p className="text-[12px] text-muted-foreground">
                  <span className="mr-1.5 inline-flex items-center rounded-full bg-surface px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground">
                    {data.rating}
                    {data.kind === "NPS_FOLLOWUP" ? "/10" : "/5"}
                  </span>
                  {data.kind === "NPS_FOLLOWUP"
                    ? data.rating >= 9
                      ? "Promoter"
                      : data.rating <= 6
                        ? "Detractor"
                        : "Passive"
                    : data.rating >= 4
                      ? "Promoter"
                      : data.rating <= 2
                        ? "Detractor"
                        : "Neutral"}
                </p>
              )}
              <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-foreground">
                {data.message}
              </p>
              {data.tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-2">
                  {data.tags.map((tag) => (
                    <span
                      key={tag}
                      className="mono inline-flex items-center rounded-sm bg-surface px-1.5 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {data.context && Object.keys(data.context).length > 0 && (
            <Card title="Context">
              <pre className="mono max-h-72 overflow-auto rounded-sm bg-surface p-3 text-[11px] text-muted-foreground">
                {JSON.stringify(data.context, null, 2)}
              </pre>
            </Card>
          )}
        </div>

        {/* Right: triage controls + identity. */}
        <div className="space-y-4">
          <Card title="From">
            {data.user ? (
              <dl className="space-y-2.5">
                <Row
                  label="Name"
                  value={
                    <Link
                      to={`/users/${data.user.id}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {data.user.name}
                      <ExternalLink className="size-3" />
                    </Link>
                  }
                />
                <Row label="Email" value={data.user.email} />
                <Row label="User ID" value={data.user.id} mono />
              </dl>
            ) : (
              <dl className="space-y-2.5">
                <Row label="Email" value={data.email ?? "—"} />
                <Row label="User" value="Anonymous (not signed in)" />
              </dl>
            )}
          </Card>

          <Card title="Triage">
            <div className="space-y-4">
              <div>
                <Label className="text-[12px] uppercase tracking-[0.06em] text-subtle">
                  Status
                </Label>
                <div className="mt-2 flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1">
                  {STATUS_OPTIONS.map((opt) => {
                    const isActive = opt.value === status;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleMarkAs(opt.value)}
                        disabled={updateMutation.isPending}
                        className={cn(
                          "inline-flex items-center rounded-sm px-2 py-0.5 text-[12px] font-medium transition-colors duration-120 ease-out",
                          isActive
                            ? "bg-surface text-foreground"
                            : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="feedback-tags"
                  className="text-[12px] uppercase tracking-[0.06em] text-subtle"
                >
                  Tags (comma-separated)
                </Label>
                <Input
                  id="feedback-tags"
                  value={tagsRaw}
                  onChange={(e) => setTagsRaw(e.target.value)}
                  placeholder="bug, ux, billing"
                  className="mt-2 h-8 text-[13px]"
                />
              </div>

              <div>
                <Label
                  htmlFor="feedback-note"
                  className="text-[12px] uppercase tracking-[0.06em] text-subtle"
                >
                  Internal note
                </Label>
                <Textarea
                  id="feedback-note"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={4}
                  placeholder="Only visible to admins."
                  className="mt-2 text-[13px]"
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/feedback")}
                  disabled={updateMutation.isPending}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || updateMutation.isPending}
                >
                  <Save className="mr-1.5 size-3.5" />
                  {updateMutation.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </Card>

          {data.repliedAt && (
            <Card title="Last reply">
              <dl className="space-y-2">
                <Row label="When" value={new Date(data.repliedAt).toLocaleString()} />
                {data.repliedById && <Row label="By" value={data.repliedById} mono />}
              </dl>
            </Card>
          )}
        </div>
      </section>

      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reply to {recipientEmail ?? "user"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="reply-subject" className="text-[12px] text-subtle">
                Subject
              </Label>
              <Input
                id="reply-subject"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="reply-body" className="text-[12px] text-subtle">
                Message
              </Label>
              <Textarea
                id="reply-body"
                rows={9}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Hi! Thanks for writing in — here's what we found…"
                className="mt-1"
              />
            </div>
            <div className="rounded-sm bg-surface p-3 text-[11px] text-muted-foreground">
              <p className="mb-1 font-semibold uppercase tracking-[0.06em] text-subtle">
                Quoted message
              </p>
              <p className="line-clamp-3 whitespace-pre-wrap">{data.message}</p>
            </div>
            {replyError && <p className="text-[12px] text-danger-foreground">{replyError}</p>}
          </div>

          <DialogFooter className="gap-2 sm:flex-row sm:justify-between">
            <Button variant="ghost" onClick={() => setReplyOpen(false)} type="button">
              <X className="mr-1.5 size-3.5" />
              Cancel
            </Button>
            <Button onClick={handleSendReply} disabled={replyMutation.isPending} type="button">
              <Reply className="mr-1.5 size-3.5" />
              {replyMutation.isPending ? "Sending…" : "Send reply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminFeedbackDetailPage;
